import { useHeaderDispatch } from 'header-utils/context/HeaderContext/useHeaderDispatch'
import { useHeaderActions } from 'header-utils/hooks/useHeaderActions'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import type { BrandId } from 'types/brands'
import type { MasterData } from 'types/masterData'
import {
	hideOverflows,
	restoreOverflows,
} from 'utils/overflowControl/overflowControl'

import type {
	MenuRefState,
	MenuRefType,
} from '../context/MenuContext/MenuRefContext'
import { useMenuDataDispatch } from '../context/MenuContext/useMenuDataDispatch'
import { useMenuDataState } from '../context/MenuContext/useMenuDataState'
import { useMenuRefDispatch } from '../context/MenuContext/useMenuRefDispatch'
import { useMenuRefState } from '../context/MenuContext/useMenuRefState'
import { useMenuStatesDispatch } from '../context/MenuContext/useMenuStatesDispatch'
import { useMenuStatesState } from '../context/MenuContext/useMenuStatesState'
import {
	type BrandEntry,
	type MenuEntry,
	MenuSectionType,
} from '../types/services/menu'

export function useMenus() {
	const {
		country: { brands },
	} = useMasterData()

	const {
		open,
		firstOpen,
		brandSelected,
		subBrandSelected,
		selectedEntry, // only used in SM
		openingWithKeyboardFocus, // only used in L+ PENDING TO REVIEW TO USE IN SM
	} = useMenuStatesState()
	const { menus } = useMenuDataState()
	const { refContent, refBrands, refCloseButton } = useMenuRefState()
	const dispatchStates = useMenuStatesDispatch()
	const dispatchData = useMenuDataDispatch()
	const dispatchRef = useMenuRefDispatch()
	const { openMenu } = useHeaderActions()
	const dispatchHeader = useHeaderDispatch()

	const refs = { refContent, refBrands, refCloseButton }

	const updateOpen = (newValue: boolean) => {
		if (newValue !== open) {
			updateFirstOpen(true)
			dispatchStates({ open: newValue })
			newValue && openMenu()
			newValue ? hideOverflows() : restoreOverflows()
		}
	}

	const updateFirstOpen = (newValue: boolean) => {
		if (newValue !== firstOpen) {
			dispatchStates({ firstOpen: newValue })
		}
	}

	const updateBrandSelected = (newValue: MasterData['brand']) => {
		if (newValue === brandSelected) {
			return
		}

		dispatchStates({ brandSelected: newValue })
		dispatchHeader({ brandId: newValue })

		const subBrands = brands?.find((brand) => brand.id === newValue)?.subBrands

		if (subBrands) {
			updateSubBrandSelected(subBrands[0].id)
		} else {
			updateSubBrandSelected(null)
		}
	}

	const updateSubBrandSelected = (newValue: MasterData['subBrand']) => {
		if (newValue !== subBrandSelected) {
			dispatchStates({ subBrandSelected: newValue })
			dispatchHeader({ subBrandId: newValue })
		}
	}

	const updateSelectedEntry = (newValue: string | null) => {
		dispatchStates({ selectedEntry: newValue })
	}

	const updateOpeningWithKeyboardFocus = (newValue: boolean) => {
		dispatchStates({ openingWithKeyboardFocus: newValue })
	}

	const updateMenus = (newValue?: MenuEntry[]) => {
		if (!menus && (newValue ?? []).length > 0) {
			dispatchData({ menus: newValue })
		}
	}

	const updateRefs = (
		refName: MenuRefType,
		newRefValue: MenuRefState[MenuRefType]
	) => {
		dispatchRef({ [refName]: newRefValue })
	}

	const getTopBrands = () => {
		if (!menus) {
			return []
		}

		return menus.map((brand) => ({
			...brand,
			menus: null,
		}))
	}

	const getBrandSubBrands = (brandId: BrandId | null) => {
		const brandEntry = menus?.find(
			(brand) => brand.id === brandId
		) as BrandEntry

		if (brandEntry?.type === MenuSectionType.Multibrand) {
			return brandEntry?.menus?.map((subBrand) => ({
				...subBrand,
			})) as BrandEntry[]
		}

		return undefined
	}

	const getBrandMenu = (
		brandId: BrandId | null,
		subBrandId: BrandId | null
	) => {
		if (subBrandId) {
			const subBrands = getBrandSubBrands(brandId)
			const subBrand = subBrands?.find((item) => item.id === subBrandId)

			return {
				menus: subBrand?.menus,
				subLabel: subBrand?.subLabelId,
			}
		}
		return {
			menus: menus?.find((brand) => brand.id === brandId)?.menus,
			subLabel: null,
		}
	}

	return {
		open,
		firstOpen,
		brandSelected,
		subBrandSelected,
		selectedEntry,
		openingWithKeyboardFocus,
		refs,
		updateMenus,
		updateOpen,
		updateFirstOpen,
		updateBrandSelected,
		updateSubBrandSelected,
		updateSelectedEntry,
		updateOpeningWithKeyboardFocus,
		updateRefs,
		getTopBrands,
		getBrandSubBrands,
		getBrandMenu,
		hasDataLoaded: !!menus,
	}
}
